import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useData, STAGES, blockers } from '../data.js';
import {
  enrichCompany, updateRecord, createRecord,
  sequenceAction, recommendCadence, findDomains, ApiError,
  type CompanyDetail, type Touch, type Deal,
} from '../api.js';
import { Strategy } from './Strategy.js';
import {
  useRecords, Text, Pick, Area, Drawer, Fold, Err, money, shortDate,
} from '../ui.js';
import { CreateMenu } from './CreateMenu.js';
import { DocumentsPanel, EditCompany } from './Panels.js';
import { Composer, type ComposerTarget } from './Composer.js';
import { Paths } from './Paths.js';
import { Console } from './Console.js';
import { Stream } from './Stream.js';
import { Fit } from './Fit.js';
import { SourcePeople } from './SourcePeople.js';

// Chat lives in the Console at the top of the record, not in a docked rail.
// It was a tab (invisible until you went looking), then a permanent right-hand
// column (a fifth place to look, on a page whose whole redesign was about having
// one). It is now one of four modes on the single input, which is where someone
// asking a question about this account would actually reach for it.

/**
 * Buying the FACTS about a company — not the people at it.
 *
 * This is all that survives of the old "Research" fold, and it is deliberately
 * tiny. That fold held two unrelated purchases in one pane: firmographics at a
 * credit per account, and contact sourcing at a credit per person. Kyle: "you
 * have the contact sourcing convoluted with Account sourcing and research."
 * People moved to their own window (SourcePeople.tsx); this stayed.
 *
 * It lives in the header now, beside the facts it fills in. A button that
 * populates industry, headcount, revenue and funding belongs next to industry,
 * headcount, revenue and funding — not eight folds below them.
 *
 * NOT THE WARM BUTTON. Email is the one warm CTA on this page. This spends
 * money, so it states its price and stays quiet — the standing rule for every
 * paid action in the app.
 */
function EnrichAccount({ c, live, onChanged }: {
  c: CompanyDetail; live: boolean; onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!live) return null;

  async function run<T>(key: string, fn: () => Promise<T>, after: (r: T) => void) {
    setBusy(key); setError(null); setNote(null);
    try { after(await fn()); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(null); }
  }

  return (
    <>
      {/* Without a domain, enrichment AND people search both refuse — Apollo
          keys on it. Offer the lookup here rather than making Kyle work out why
          a button returned 400. */}
      {/* domain OR url — the server derives from either; gating on url alone
          hid the enrich button on accounts added by name with only a domain. */}
      {!c.url && !(c as unknown as { domain?: string }).domain ? (
        <button className="linkish" disabled={busy !== null} onClick={() =>
          void run('domain', () => findDomains(c.slug), (r) => {
            const s = r.suggestions[0];
            setNote(s?.domain
              ? `Suggested ${s.domain} (${s.confidence} confidence) — ${s.reasoning}. `
                + 'Not saved: open Edit company and set it if that looks right.'
              : `Could not identify it. ${s?.reasoning ?? ''}`);
          })
        }>
          {busy === 'domain' ? 'Looking…' : 'Find their website'}
        </button>
      ) : (
        <button className="linkish" disabled={busy !== null} onClick={() =>
          void run('company', () => enrichCompany(c.slug), () => {
            setNote('Account enriched. Blank fields filled; your own research kept.');
            onChanged();
          })
        }>
          {busy === 'company' ? 'Enriching…' : 'Enrich account'}
          <span className="cost">1 credit</span>
        </button>
      )}
      {note && <p className="ok-note tight">{note}</p>}
      {error && <p className="error tight">{error}</p>}
    </>
  );
}

/**
 * Deals take a fold only when there ARE deals.
 *
 * There are zero deals across all 28 accounts in this CRM — the practice is
 * still entirely outbound. So on every record ever opened, "Deals" was a
 * disclosure triangle you could click to be told there was nothing behind it.
 * That is the "wall of data" Kyle reported, and it is made of empty containers
 * rather than full ones.
 *
 * The ACTION does not disappear with the container. An account with no deals
 * gets one quiet line that opens one, which is strictly more useful than a fold
 * hiding the same button. When money does land on the account it earns its fold
 * back automatically.
 */
function DealsFold({ companyId }: { companyId: string }) {
  const deals = useRecords<Deal & { id: string; next_step: string | null }>(
    'deals', { company_id: companyId });
  const [creating, setCreating] = useState(false);

  const menu = creating && (
    <CreateMenu initial="deal" companyId={companyId}
      onClose={() => { setCreating(false); void deals.reload(); }} />
  );

  // Still loading: render nothing rather than flashing "no deals" at somebody
  // who has three.
  if (!deals.rows) return null;

  if (deals.rows.length === 0) {
    // data-fold="deals" even on the empty line: the Console's "Open a deal"
    // chip targets it, and a missing anchor made that chip a silent no-op on
    // every account without deals — the only accounts where you'd click it.
    return (
      <>
        <p className="foldnone" data-fold="deals">
          No deals on this account.{' '}
          <button className="linkish" onClick={() => setCreating(true)}>Open one</button>{' '}
          when there is real money on the table — that is the line between
          outreach and revenue.
        </p>
        {menu}
      </>
    );
  }

  const open = deals.rows.reduce((n, d) => n + (Number(d.amount) || 0), 0);

  return (
    <Fold
      title="Deals"
      count={deals.rows.length}
      summary={`${money(open)} on the table`}
    >
      {deals.rows.map((d) => (
        <div className="event" key={d.id}>
          <div className="row">
            <span className="rname">{d.name}</span>
            <span className="flag flag-mute">{d.stage}</span>
            <span className="meta">{money(d.amount)}</span>
            <span className="meta">{shortDate(d.expected_close)}</span>
          </div>
          {d.next_step && <p className="meta">Next: {d.next_step}</p>}
        </div>
      ))}
      <div className="actions">
        <button onClick={() => setCreating(true)}>Add another deal</button>
      </div>
      {menu}
    </Fold>
  );
}

function Prop({ label, value }: { label: string; value: unknown }) {
  if (value === null || value === undefined || value === '' ) return null;
  const text = Array.isArray(value) ? value.join(', ') : String(value);
  if (!text) return null;
  return (
    <div className="prop">
      <span>{label}</span>
      <strong>{text}</strong>
    </div>
  );
}

function TouchRow({ t, open, onToggle, onEdit }: {
  t: Touch; open: boolean; onToggle: () => void; onEdit: () => void;
}) {
  const written = Boolean(t.body);
  return (
    <>
      <tr className={written ? 'written' : ''} onClick={written ? onToggle : undefined}>
        <td className="day">d{t.day_offset}</td>
        <td className="who">{t.contact_name?.split(' ')[0] ?? '—'}</td>
        <td className="chan">{t.channel}</td>
        <td className="act">{t.act ? `act ${t.act}` : ''}</td>
        <td className="subj">
          {t.subject ?? <span className="meta">{t.notes?.split('.')[0] ?? 'not written'}</span>}
        </td>
        <td className="st">
          <span className={`flag ${written ? 'flag-ok' : 'flag-mute'}`}>
            {written ? 'written' : t.status}
          </span>
        </td>
        <td className="st">
          {/* stopPropagation, or the row's expand toggle swallows the click. */}
          <button className="mini"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}>
            {written ? 'Edit' : 'Write'}
          </button>
        </td>
      </tr>
      {open && t.body && (
        <tr className="bodyrow">
          <td colSpan={7}>
            <pre className="emailbody">{t.body}</pre>
            {t.notes && <p className="tnote">{t.notes}</p>}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Build and arm a cadence.
 *
 * Arming is a transition with gates behind it (sequence-core.ts): conflict gate
 * clear, at least one touch, nobody suppressed, every email touch holding a real
 * address. The button does not pre-check any of that — the server is the
 * authority, and a client-side copy of the rules is a client-side copy that
 * drifts. It just shows what came back.
 *
 * Copy is deliberately NOT a gate. Kyle writes as the cadence runs; an unwritten
 * touch simply waits instead of sending a blank email.
 */
function SequenceControls({ c, onChanged }: { c: CompanyDetail; onChanged: () => void }) {
  const seqs = useRecords<{ id: string; status: string; mode: string; pause_reason: string | null }>(
    'sequences', { company_id: c.id });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const seq = seqs.rows?.[0] ?? null;
  const written = c.touches.filter((t) => t.body).length;

  async function act(action: 'arm' | 'pause' | 'resume' | 'cancel') {
    if (!seq) return;
    setBusy(action); setError(null); setMsg(null);
    try {
      const r = await sequenceAction(seq.id, action);
      setMsg(action === 'arm' || action === 'resume'
        ? `Armed — ${r.touches} touch(es) dated, first due ${r.first_due}.`
        : `Sequence ${r.status}.`);
      void seqs.reload(); onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  if (!seq) {
    return (
      <div className="seqbar">
        <div className="seqbar-text">
          <strong>No sequence on this account</strong>
          <span>A sequence holds the cadence. Touches hang off it, and arming dates them.</span>
        </div>
        <button className="primary" disabled={busy === 'new'} onClick={async () => {
          setBusy('new'); setError(null);
          try {
            await seqs.create({
              company_id: c.id, mode: 'A',
              activation_rule: 'Any real dialogue pauses every remaining cold touch on the whole account.',
            });
            void seqs.reload();
          } catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
          finally { setBusy(null); }
        }}>Start a sequence</button>
        <Err>{error}</Err>
      </div>
    );
  }

  return (
    <>
      <div className={`seqbar ${seq.status}`}>
        <div className="seqbar-text">
          <strong>
            Sequence · {seq.status}
            {seq.status === 'draft' && ' — not scheduled'}
          </strong>
          <span>
            {c.touches.length} touch{c.touches.length === 1 ? '' : 'es'}, {written} written.
            {seq.pause_reason ? ` ${seq.pause_reason}.` : ''}
            {' '}Unwritten touches wait rather than sending blank.
          </span>
        </div>
        <div className="seqbar-actions">
          {seq.status === 'draft' && c.touches.length === 0 && (
            <button className="primary" disabled={busy === 'rec' || c.contacts.length === 0}
              title={c.contacts.length
                ? 'Propose a cadence from the research, the ICP and the committee'
                : 'Needs contacts to aim at'}
              onClick={async () => {
                setBusy('rec'); setError(null); setMsg(null);
                try {
                  const r = await recommendCadence(seq.id);
                  setMsg(`${r.rationale}\n\n${r.touches} touches drafted. ${r.note}`
                    + (r.dropped.length ? `\nDropped: ${r.dropped.join(', ')}` : ''));
                  onChanged();
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : String(e));
                } finally { setBusy(null); }
              }}>
              {busy === 'rec' ? 'Thinking…' : 'Recommend a cadence'}
            </button>
          )}
          <button onClick={() => setAdding(true)}>Add touch</button>
          {seq.status === 'draft' && (
            <button className="primary" disabled={busy === 'arm'} onClick={() => void act('arm')}>
              {busy === 'arm' ? 'Arming…' : 'Arm sequence'}
            </button>
          )}
          {(seq.status === 'armed' || seq.status === 'active') && (
            <button disabled={busy === 'pause'} onClick={() => void act('pause')}>Pause</button>
          )}
          {seq.status === 'paused' && (
            <button className="primary" disabled={busy === 'resume'} onClick={() => void act('resume')}>
              Resume
            </button>
          )}
        </div>
      </div>
      {msg && <p className="ok-note">{msg}</p>}
      <Err>{error}</Err>
      {adding && (
        <AddTouch sequenceId={seq.id} contacts={c.contacts}
          nextRef={`T${c.touches.length + 1}`}
          onClose={() => setAdding(false)} onSaved={onChanged} />
      )}
    </>
  );
}

function AddTouch({ sequenceId, contacts, nextRef, onClose, onSaved }: {
  sequenceId: string; contacts: CompanyDetail['contacts']; nextRef: string;
  onClose: () => void; onSaved: () => void;
}) {
  const [f, setF] = useState({
    ref: nextRef, day_offset: '1', channel: 'email',
    contact_id: contacts[0]?.id ?? '', subject: '', body: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Drawer title="Add a touch" subtitle="Day offset is relative to arming, not a calendar date"
      onClose={onClose} footer={
        <button className="primary" disabled={saving || !f.ref.trim()} onClick={async () => {
          setSaving(true); setError(null);
          try {
            await createRecord('touches', {
              sequence_id: sequenceId, ref: f.ref.trim(),
              day_offset: Number(f.day_offset) || 1, channel: f.channel,
              contact_id: f.contact_id || null,
              subject: f.subject || null, body: f.body || null, notes: f.notes || null,
            });
            onSaved(); onClose();
          } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e)); setSaving(false);
          }
        }}>{saving ? 'Saving…' : 'Add touch'}</button>
      }>
      <Err>{error}</Err>
      <div className="pair">
        <Text label="Ref" value={f.ref} onChange={(e) => setF((p) => ({ ...p, ref: e.target.value }))} />
        <Text label="Day" type="number" value={f.day_offset}
          hint="1 = the day it arms."
          onChange={(e) => setF((p) => ({ ...p, day_offset: e.target.value }))} />
      </div>
      <div className="pair">
        <Pick label="Channel" value={f.channel} allowEmpty={false}
          options={['email', 'linkedin', 'call', 'video', 'other'].map((v) => ({ value: v, label: v }))}
          onChange={(v) => setF((p) => ({ ...p, channel: v }))} />
        <Pick label="Who" value={f.contact_id}
          options={contacts.map((x) => ({ value: x.id, label: x.name }))}
          onChange={(v) => setF((p) => ({ ...p, contact_id: v }))} />
      </div>
      <Text label="Subject" value={f.subject}
        onChange={(e) => setF((p) => ({ ...p, subject: e.target.value }))} />
      <Area label="Body" rows={10} value={f.body}
        placeholder="Leave blank for now — an unwritten touch waits rather than sending."
        onChange={(v) => setF((p) => ({ ...p, body: v }))} />
    </Drawer>
  );
}

/** Read a column off a record whose TypeScript type predates it. */
function str(o: unknown, k: string): string {
  const v = (o as Record<string, unknown>)[k];
  return v == null ? '' : String(v);
}

/** "Pittsburgh, PA, USA" from whichever parts are filled in. */
function place(c: unknown): string {
  return [str(c, 'city'), str(c, 'state'), str(c, 'country')].filter(Boolean).join(', ');
}

/** Bare domain from whatever the record actually has. */
function domainOf(c: { domain?: string | null; url?: string | null }): string {
  const raw = c.domain ?? c.url ?? '';
  return raw.trim().toLowerCase()
    .replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');
}

/**
 * Company mark.
 *
 * Tries the logo Apollo stored, then a favicon lookup, then falls back to
 * initials. The fallback is not a nicety — most of these are private mid-market
 * companies and a broken-image icon on a third of the pipeline looks worse than
 * no logos at all.
 */
function CompanyLogo({ name, domain }: { name: string; domain: string }) {
  const [failed, setFailed] = useState(false);
  const initials = name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  if (!domain || failed) {
    return <div className="cologo cologo-fallback" aria-hidden="true">{initials}</div>;
  }
  return (
    <img
      className="cologo"
      src={`https://www.google.com/s2/favicons?sz=128&domain=${encodeURIComponent(domain)}`}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

/**
 * The one line that says what to do next.
 *
 * Ordered by what actually stops the work: a hard block beats a missing
 * coordinate, which beats unwritten copy, which beats a stage that has gone
 * stale. Falls back to Kyle's own `next_action` when he has set one — a human
 * decision outranks anything derived.
 */
function NextAction({ c, blockers: b, onGo, onSource }: {
  c: CompanyDetail; blockers: { label: string; hard: boolean }[];
  onGo: (section: string) => void;
  /** Opens the sourcing window. See the `go` note on the CTA below. */
  onSource: () => void;
}) {
  const withEmail = c.contacts.filter((x) => x.email).length;
  const unwritten = c.touches.filter((t) => !t.body).length;
  const hard = b.find((x) => x.hard);

  let what: string;
  let why: string;
  // A CTA either SCROLLS to a fold or DOES the thing. "Find contacts" used to
  // scroll — which delivered you to a second button with the same label, the
  // exact defect Kyle reported. Anything that can just happen now just happens.
  let cta: { label: string; tab?: string; go?: () => void } | null = null;

  // "no coordinates" IS the missing-address state wearing a blocker costume,
  // and it made the address branch below unreachable: the CTA read "See the
  // argument" for a problem the argument cannot fix. A hard blocker only
  // outranks the chain when it is not the same fact restated.
  if (hard && hard.label !== 'no coordinates') {
    what = `Clear the block: ${hard.label}`;
    why = 'Nothing sends while a hard gate is open.';
    cta = { label: 'See the argument', tab: 'the-argument' };
  } else if (c.contacts.length === 0) {
    what = 'Find the buying committee';
    why = 'No contacts on this account yet — there is nobody to write to.';
    cta = { label: 'Find people', go: onSource };
  } else if (withEmail === 0) {
    what = 'Get an address for someone';
    why = `${c.contacts.length} contact${c.contacts.length > 1 ? 's' : ''}, none with an email.`;
    cta = { label: 'Get an address', tab: 'ways-in' };
  } else if (c.next_action) {
    what = c.next_action;
    why = c.next_action_date
      ? `You set this${' '}${new Date(c.next_action_date) < new Date() ? '— now overdue' : `for ${shortDate(c.next_action_date)}`}.`
      : 'You set this on the account.';
  } else if (unwritten > 0) {
    what = `Write the remaining ${unwritten} touch${unwritten > 1 ? 'es' : ''}`;
    why = 'The sequence is planned but the copy is not written.';
    cta = { label: 'Open sequence', tab: 'sequence' };
  } else if (c.touches.length === 0) {
    what = 'Design the sequence';
    why = 'You have people and addresses. Nothing is planned yet.';
    cta = { label: 'Open sequence', tab: 'sequence' };
  } else {
    what = 'Ready to send';
    why = 'Committee mapped, copy written, nothing blocking.';
    cta = { label: 'Review sequence', tab: 'sequence' };
  }

  return (
    <div className={`nextbar ${hard ? 'blocked' : ''}`}>
      <div className="nextbar-icon" aria-hidden="true">{hard ? '!' : '→'}</div>
      <div className="nextbar-text">
        <strong>{what}</strong>
        <span>{why}</span>
      </div>
      {cta && (
        <button className="primary"
          onClick={() => (cta!.go ? cta!.go() : onGo(cta!.tab!))}>
          {cta.label}
        </button>
      )}
    </div>
  );
}

export function Record() {
  const { slug = '' } = useParams();
  // Today's "Find the committee" batch lands here with ?find=people so the
  // click that promised sourcing DELIVERS sourcing — not a page with the
  // right button somewhere on it. Consumed once, then stripped, so refresh
  // and back-button do not reopen a window the operator closed.
  const [search, setSearch] = useSearchParams();
  const { loadCompany, live } = useData();
  const [c, setC] = useState<CompanyDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [sourcing, setSourcing] = useState(false);
  useEffect(() => {
    if (search.get('find') === 'people') {
      setSourcing(true);
      setSearch({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [composing, setComposing] =
    useState<ComposerTarget['existing'] | 'new' | null>(null);
  // Who the composer should open addressed to. Stream's Reply knows exactly
  // which human the message came from; dropping that and defaulting to "first
  // contact with an email" drafted replies to the WRONG PERSON on any
  // multi-contact account — found by the interaction audit, and exactly the
  // kind of misdirected mail no gate downstream can catch.
  const [composeTo, setComposeTo] = useState<string | null>(null);
  const [openTouch, setOpenTouch] = useState<string | null>(null);

  const openFold = (id: string) => {
    const el = document.querySelector(`[data-fold="${id}"]`);
    if (!el) return;
    const head = el.querySelector('.fold-head') as HTMLButtonElement | null;
    if (head?.getAttribute('aria-expanded') === 'false') head.click();
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // Bumped after an enrichment write so the record re-reads from the API and
  // the committee rail shows the contact that was just revealed.
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadCompany(slug).then((r) => {
      if (cancelled) return;
      if (r) { setC(r); setMissing(false); }
      else setMissing(true);
    });
    return () => { cancelled = true; };
  }, [slug, live, reload]);

  if (missing) return <p className="empty">No account: {slug}</p>;
  if (!c) return <p className="loading">Loading…</p>;

  const b = blockers(c);
  const researched = c.touches.length > 0 || Boolean(c.intent_why);

  return (
    <div className="record">
      <Link className="back" to="/companies">← Pipeline</Link>

      {/* WHO THIS IS — the company, above the work. Everything here is a fact
          about them; nothing here is an action of ours. Keeping those two
          separate is the whole point of the re-layout. */}
      <header className="cohead">
        <CompanyLogo name={c.name} domain={domainOf(c)} />
        <div className="cohead-main">
          <div className="cohead-title">
            <h1>{c.name}</h1>
            {c.priority && <span className={`pri pri-${c.priority.toLowerCase()}`}>{c.priority}</span>}
            {/* The hand-typed `fit_score` chip used to live here. It was one
                person's judgement of 28 accounts they already knew, which does
                not survive being handed to anyone else. The computed verdict
                sits under the facts instead, where it has room to justify
                itself — see Fit.tsx. */}
          </div>
          {c.description && <p className="codesc">{c.description}</p>}
          <dl className="cofacts">
            {str(c, 'industry') && <><dt>Industry</dt><dd>{str(c, 'industry')}</dd></>}
            {c.sub_category && <><dt>Category</dt><dd>{c.sub_category}</dd></>}
            {(place(c) || c.hq) && <><dt>Where</dt><dd>{place(c) || c.hq}</dd></>}
            {c.employees && <><dt>Size</dt><dd>{c.employees} people</dd></>}
            {c.founded && <><dt>Founded</dt><dd>{c.founded}</dd></>}
            {(str(c, 'revenue') || c.revenue_band) && (
              <><dt>Revenue</dt>
                <dd>{str(c, 'revenue') ? money(Number(str(c, 'revenue'))) : c.revenue_band}</dd></>
            )}
            {str(c, 'phone') && <><dt>Phone</dt><dd>{str(c, 'phone')}</dd></>}
            {c.funding_stage && <><dt>Funding</dt><dd>{c.funding_stage}</dd></>}
            {c.total_raised && <><dt>Raised</dt><dd>{c.total_raised}</dd></>}
            {c.sponsor && <><dt>Sponsor</dt><dd>{c.sponsor}</dd></>}
            {domainOf(c) && (
              <>
                <dt>Web</dt>
                <dd>
                  <a href={`https://${domainOf(c)}`} target="_blank" rel="noreferrer">
                    {domainOf(c)}
                  </a>
                </dd>
              </>
            )}
          </dl>
          <Fit fit={(c as unknown as { fit?: never }).fit}
            icpName={(c as unknown as { icp_name?: string }).icp_name} />
        </div>
        <div className="cohead-side">
          <label className="fieldlabel">Stage</label>
          <select
            value={c.stage}
            onChange={(ev) => void updateRecord('companies', c.id, { stage: ev.target.value })
              .then(() => setReload((n) => n + 1))}
          >
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button className="primary" disabled={c.contacts.length === 0}
            title={c.contacts.length ? 'Write to someone on this account'
              : 'No contacts on this account yet'}
            onClick={() => setComposing('new')}>Email</button>
          <button onClick={() => setEditing(true)}>Edit company</button>
          {/* The button that fills industry, size, revenue and funding now sits
              beside industry, size, revenue and funding. It used to live eight
              folds below them, sharing a pane with contact sourcing. */}
          <EnrichAccount c={c} live={live} onChanged={() => setReload((n) => n + 1)} />
        </div>
      </header>

      {/* WHAT TO DO — one line, always present. A record that shows you
          everything and tells you nothing is why this page was hard to read. */}
      <NextAction
        c={c}
        blockers={b}
        onSource={() => setSourcing(true)}
        // The tabs are gone, so "go do it" means scroll to the fold that holds
        // the work rather than switching a view. Same intent, one less concept.
        // "Open sequence" must OPEN the sequence. This used to smooth-scroll
        // to a collapsed fold header and dispatch a CustomEvent whose listener
        // had been deleted — the audit's phrase was "scrolls to a shut door".
        // openFold is the function the Console has used all along: expand,
        // then scroll.
        onGo={openFold}
      />

      {b.length > 0 && (
        <div className="blockbar">
          {b.map((x) => (
            <span key={x.label} className={`flag ${x.hard ? 'flag-block' : 'flag-warn'}`}>
              {x.label}
            </span>
          ))}
        </div>
      )}

      <div className="recbody">
          {/* ONE INPUT, FOUR INTENTS. See Console.tsx — the page now meets an
              intent instead of asking which container it lives in. */}
          <Console
            c={c}
            onCompose={() => setComposing('new')}
            onChanged={() => setReload((n) => n + 1)}
            onOpenFold={openFold}
          />

          <Fold
            title="What happened"
            id="stream"
            defaultOpen
            summary={`${c.touches.filter((t) => t.status === 'sent').length || 'no'} touches sent`}
          >
          <Stream
            hideComposer
            companyId={c.id}
            contacts={c.contacts}
            onCompose={() => setComposing('new')}
            onLogged={() => setReload((n) => n + 1)}
            onReply={(m) => {
              setComposeTo(m.contactId);
              setComposing({
                id: '',
                subject: m.subject,
                body: ['', '', '---', ...m.quote.split('\n').map((l) => `> ${l}`)].join('\n'),
                kind: 'email',
              });
            }}
          />
          </Fold>

          {/* Open questions lost their rail and gained urgency by it: these are
              things waiting on Kyle, so they sit above the folds rather than in
              a column he had learned to stop seeing. */}
          {c.open_questions.length > 0 && (
            <ul className="blockers openq">
              {c.open_questions.map((q) => (
                <li key={q.id} className={q.blocking ? 'hard' : ''}>
                  {q.question.split(' — ')[0]}
                  <span className="meta">{q.question.split(' — ').slice(1).join(' — ')}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Everything else folds. Each header carries its own answer, so the
              common case is reading the summary and never expanding at all. */}
          <div className="folds">
            <Fold
              id="ways-in"
              title="Ways in"
              summary={c.contacts.length
                ? `${c.contacts.length} on the account`
                : 'nobody mapped yet'}
              tone={c.contacts.length === 0 ? 'warn' : null}
              defaultOpen={c.contacts.length === 0}
            >
              {/* The committee used to sit in a permanent right rail. It belongs
                  here: "who is on this account" and "how do I reach them" are
                  the same question, and answering it twice in two places is how
                  a page starts feeling like a register. */}
              {/* ADD SOMEBODY YOU ALREADY KNOW.
                  Kyle reads a leadership page and types the decision-makers in
                  before any provider has heard of them. That path existed only
                  behind the global "+ Create" in the nav — three screens from
                  the account he was looking at — so from here the only way to
                  get a person onto an account was to buy one. Adding by hand is
                  free, it is often FIRST, and the import now recognises the same
                  human when it arrives later (see name-match.ts). */}
              <section>
                <h4>
                  On the account
                  <span className="count">
                    {c.contacts.length === 0 ? 'nobody yet'
                      : `${c.contacts.filter((p) => p.email).length} of ${c.contacts.length} reachable`}
                  </span>
                </h4>

                {c.contacts.length === 0 && (
                  <p className="meta">
                    Search Apollo for the committee, or type in whoever you found on
                    their leadership page. Both are free, and an Apollo import later
                    recognises the same person rather than duplicating them.
                  </p>
                )}

                {c.contacts.map((p) => (
                  <div className="person" key={p.id}>
                    <div>
                      <Link to={`/contacts/${p.id}`}><strong>{p.name}</strong></Link>
                      <span className="meta">{p.title}</span>
                    </div>
                    {/* A missing address is the thing that stops a sequence, so
                        it is a route to the fix rather than a red label. */}
                    {p.email
                      ? <span className="flag flag-ok">{p.email}</span>
                      : (
                        <Link className="mini" to={`/contacts/${p.id}`}>
                          no address — get one
                        </Link>
                      )}
                  </div>
                ))}

                {/* TWO WAYS A PERSON GETS HERE, AND BOTH ARE FREE.
                    Apollo, for the committee you have not met; by hand, for the
                    names off a leadership page. Sourcing used to be a fold at
                    the bottom of the page sharing a pane with account
                    enrichment — see SourcePeople.tsx for why it is a window. */}
                <div className="actions">
                  <button onClick={() => setSourcing(true)}>Find people in Apollo</button>
                  <span className="cost">free to search</span>
                  <button onClick={() => setAddingContact(true)}>Add someone by hand</button>
                  <span className="cost">free</span>
                </div>
              </section>
              <section>
                <h4>Routes in</h4>
                <Paths slug={c.slug} />
              </section>
            </Fold>

            <Fold
              title="Sequence"
              count={c.touches.length || null}
              summary={c.touches.length
                ? `${c.touches.filter((t) => t.body).length} of ${c.touches.length} written`
                : 'nothing planned'}
              tone={c.touches.length && c.touches.some((t) => !t.body) ? 'warn' : null}
            >
              <SequenceControls c={c} onChanged={() => setReload((n) => n + 1)} />
              {c.touches.length === 0 && <p className="empty">No sequence designed yet.</p>}
              {c.touches.length > 0 && (
                <table className="touches">
                  <tbody>
                    {c.touches.map((t) => (
                      <TouchRow
                        key={t.id}
                        t={t}
                        open={openTouch === t.id}
                        onToggle={() => setOpenTouch(openTouch === t.id ? null : t.id)}
                        onEdit={() => setComposing({
                          id: t.id, subject: t.subject, body: t.body,
                          body_html: (t as { body_html?: string | null }).body_html,
                          kind: 'touch',
                        })}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </Fold>

            {/* Deals decide their own shape — see DealsFold. There are zero
                deals across all 28 accounts, so on every single record this was
                a fold you opened to be told nothing was there. */}
            <DealsFold companyId={c.id} />

            {/* THE SETTLED READING — research, decisions and the agent that
                writes them, in one fold instead of two.
                "The argument" and "Decisions" were separate containers holding
                the same act: why we believe this account is worth working, and
                what we concluded. Decisions exist on ONE account of 28, so the
                second fold was an empty box on 27 records. */}
            <Fold
              title="The argument"
              summary={c.gap_statement
                ? c.gap_statement.slice(0, 92) + (c.gap_statement.length > 92 ? '…' : '')
                : 'not researched yet'}
              tone={researched ? null : 'warn'}
            >
              {c.gap_statement && (
                <section><h4>The gap</h4><p className="gap">{c.gap_statement}</p></section>
              )}
              {c.wedge && <section><h4>Wedge</h4><p>{c.wedge}</p></section>}
              {c.intent_why && <section><h4>Why this account</h4><p>{c.intent_why}</p></section>}
              {c.intent_win && <section><h4>What a win looks like</h4><p>{c.intent_win}</p></section>}
              {c.intent_framing && (
                <section><h4>Kyle's framing</h4><p className="quote">{c.intent_framing}</p></section>
              )}
              {c.active_triggers.length > 0 && (
                <section>
                  <h4>Triggers</h4>
                  <ul>{c.active_triggers.map((t, i) => <li key={i}>{t}</li>)}</ul>
                </section>
              )}
              {c.hooks.length > 0 && (
                <section>
                  <h4>Hooks</h4>
                  <ul>{c.hooks.map((h, i) => <li key={i}>{h}</li>)}</ul>
                </section>
              )}
              {c.anecdotes.length > 0 && (
                <section>
                  <h4>Anecdotes</h4>
                  <ul>{c.anecdotes.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </section>
              )}

              {c.decisions.length > 0 && (
                <section>
                  <h4>
                    Decided
                    <span className="count">{c.decisions.length}</span>
                  </h4>
                  {c.decisions.map((d) => (
                    <div className="decision" key={d.id}>
                      <p className="what">{d.decision}</p>
                      <p className="meta">
                        {d.decided_by} · {d.decided_on} ·{' '}
                        <span className={`flag ${d.status === 'settled' ? 'flag-mute' : 'flag-warn'}`}>
                          {d.status}
                        </span>
                      </p>
                      {d.reasoning && <p className="reason">{d.reasoning}</p>}
                      {d.rejected && <p className="rejected">Rejected: {d.rejected}</p>}
                    </div>
                  ))}
                </section>
              )}

              {/* The agent that fills everything above. It sits at the bottom of
                  the fold rather than the top: on a researched account the
                  reading comes first and the rerun is a tool, not the headline. */}
              <Strategy slug={c.slug} hasWedge={Boolean(c.wedge)}
                onApplied={() => setReload((n) => n + 1)} />
            </Fold>

            {/* REFERENCE — the facts you check twice a quarter, in one fold.
                Firmographics and Documents were two containers. Across the whole
                CRM there are zero documents, and of the seven firmographic
                fields exactly one (competitors) is filled on more than a single
                account — so this was two disclosure triangles guarding one line
                of text. The enrichment that FILLS these moved to the header,
                beside the facts it writes. */}
            <Fold
              title="Details"
              summary={[
                [c.ownership_type, c.motion, c.hamer_tier && `${c.hamer_tier} tier`]
                  .filter(Boolean).join(' · ') || null,
                c.enriched_at ? `enriched ${String(c.enriched_at).slice(0, 10)}` : null,
              ].filter(Boolean).join(' · ') || 'firmographics and documents'}
            >
              <section>
                <h4>Firmographics</h4>
                <Prop label="Last round" value={
                  c.last_round_amount ? `${c.last_round_amount} ${c.last_round_lead ?? ''}`.trim() : null
                } />
                <Prop label="Ownership" value={c.ownership_type} />
                <Prop label="Motion" value={c.motion} />
                <Prop label="Marketing" value={c.leadership_state} />
                <Prop label="Competitors" value={c.competitors} />
                <Prop label="Tier" value={c.hamer_tier} />
                <Prop label="Source" value={c.source} />
              </section>
              <section>
                <h4>Documents</h4>
                <DocumentsPanel companyId={c.id} />
              </section>
            </Fold>
          </div>
        </div>


      {editing && (
        <EditCompany c={c} onClose={() => setEditing(false)}
          onSaved={() => setReload((n) => n + 1)} />
      )}

      {/* Scoped to THIS account, so the form opens with it already chosen —
          picking the company you are standing on is a question the screen
          already knows the answer to. */}
      {addingContact && (
        <CreateMenu
          initial="contact"
          companyId={c.id}
          onClose={() => { setAddingContact(false); setReload((n) => n + 1); }}
        />
      )}

      {sourcing && (
        <SourcePeople
          company={c}
          onClose={() => setSourcing(false)}
          onChanged={() => setReload((n) => n + 1)}
        />
      )}

      {composing && (
        <Composer
          target={{
            companyId: c.id,
            contactId: composeTo
              ?? c.contacts.find((x) => x.email)?.id ?? c.contacts[0]?.id ?? '',
            recipients: c.contacts.map((x) => ({
              id: x.id, name: x.name, email: x.email,
              title: x.title, suppressed: x.suppressed,
            })),
            existing: composing === 'new' ? null : composing,
          }}
          onClose={() => { setComposing(null); setComposeTo(null); }}
          onSaved={() => setReload((n) => n + 1)}
        />
      )}
    </div>
  );
}
